import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  memo,
  ReactNode,
} from "react";
import styled from "styled-components";
import { handleError, getUserFriendlyErrorMessage } from "utils/errorHandler";

export interface Message {
  id: string;
  type: "user" | "bot";
  content: string;
  source?: "profile" | "llm";
  timestamp: number;
}

interface ChatModalProps {
  isOpen: boolean;
  onClose: () => void;
  theme?: {
    accentColor?: string;
    accentBright?: string;
    backgroundColor?: string;
    textColor?: string;
  };
  isDarkTheme?: boolean;
  onSendMessage: (message: string) => Promise<string>;
  onContact?: (data: {
    name: string;
    email: string;
    message: string;
  }) => Promise<boolean>;
}

const Overlay = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: ${(props) => (props.$isOpen ? "flex" : "none")};
  align-items: flex-end;
  justify-content: flex-end;
  z-index: 1000;
  padding: 20px;
  will-change: opacity;

  @media (max-width: 768px) {
    padding: 0;
    align-items: stretch;
    justify-content: stretch;
  }
`;

const ModalContainer = styled.div<{ $isDarkTheme?: boolean; $isOpen: boolean }>`
  width: 420px;
  height: 600px;
  background: ${(props) => (props.$isDarkTheme ? "#1a1a1a" : "white")};
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  box-shadow: 0 5px 40px rgba(0, 0, 0, 0.16);
  animation: ${(props) =>
    props.$isOpen ? "slideIn 0.3s ease" : "slideOut 0.3s ease"};

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }

  @keyframes slideIn {
    from {
      opacity: 0;
      transform: translateY(100%);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes slideOut {
    from {
      opacity: 1;
      transform: translateY(0);
    }
    to {
      opacity: 0;
      transform: translateY(100%);
    }
  }

  @media (max-width: 768px) {
    width: 100%;
    height: 100%;
    border-radius: 0;
  }
  @media (max-width: 420px) {
    width: 100%;
    height: 100%;
  }
`;

const Header = styled.div<{
  $isDarkTheme?: boolean;
  $accentColor?: string;
}>`
  background: ${(props) => props.$accentColor || "#ff006e"};
  color: white;
  padding: 16px;
  border-radius: 12px 12px 0 0;
  display: flex;
  justify-content: space-between;
  align-items: center;

  @media (max-width: 768px) {
    border-radius: 0;
  }
`;

const HeaderTitle = styled.h2`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: white;
  font-size: 24px;
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.8;
  }
`;

const MessageContainer = styled.div<{ $isDarkTheme?: boolean }>`
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  will-change: scroll-position;
  overscroll-behavior: contain;
  background: ${(props) => (props.$isDarkTheme ? "#0f0f0f" : "#f5f5f5")};

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: ${(props) => (props.$isDarkTheme ? "#444" : "#ccc")};
    border-radius: 3px;
  }
`;

const MessageBubble = styled.div<{
  $isUser: boolean;
  $isDarkTheme?: boolean;
  $accentColor?: string;
}>`
  display: flex;
  width: 100%;
  justify-content: ${(props) => (props.$isUser ? "flex-end" : "flex-start")};
  align-items: flex-start;
  gap: 8px;
`;

const MessageText = styled.div<{
  $isUser: boolean;
  $isDarkTheme?: boolean;
  $accentColor?: string;
}>`
  max-width: 80%;
  padding: 12px 14px;
  border-radius: 12px;
  /* allow long words/urls to wrap instead of overflowing */
  overflow-wrap: anywhere;
  word-wrap: break-word;
  white-space: pre-wrap;
  font-size: 14px;
  line-height: 1.4;

  @media (max-width: 480px) {
    max-width: 90%;
    padding: 10px;
    font-size: 13px;
  }
  background: ${(props) => {
    if (props.$isUser) {
      return props.$accentColor || "#ff006e";
    }
    return props.$isDarkTheme ? "#2a2a2a" : "white";
  }};
  color: ${(props) => {
    if (props.$isUser) return "white";
    return props.$isDarkTheme ? "white" : "#1a1a1a";
  }};
  box-shadow: ${(props) =>
    props.$isUser
      ? "0 2px 8px rgba(0, 0, 0, 0.15)"
      : "0 2px 8px rgba(0, 0, 0, 0.08)"};

  a {
    color: ${(props) =>
      props.$isUser ? "inherit" : props.$accentColor || "#ff006e"};
    text-decoration: underline;
  }

  p {
    margin: 0 0 8px 0;
  }

  ul {
    margin: 0 0 8px 16px;
    padding-left: 12px;
  }

  li {
    margin-bottom: 4px;
  }
`;

const TypingIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 12px 14px;
  background: #2a2a2a;
  border-radius: 12px;
  width: fit-content;

  span {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #888;
    animation: typing 1.4s infinite;

    &:nth-child(2) {
      animation-delay: 0.2s;
    }

    &:nth-child(3) {
      animation-delay: 0.4s;
    }

    @media (prefers-reduced-motion: reduce) {
      animation: none;
      opacity: 0.7;
    }
  }

  @keyframes typing {
    0%,
    60%,
    100% {
      opacity: 0.5;
    }
    30% {
      opacity: 1;
    }
  }
`;

const InputContainer = styled.div<{ $isDarkTheme?: boolean }>`
  padding: 12px;
  border-top: 1px solid ${(props) => (props.$isDarkTheme ? "#333" : "#e0e0e0")};
  display: flex;
  gap: 8px;
  background: ${(props) => (props.$isDarkTheme ? "#1a1a1a" : "white")};
  border-radius: 0 0 12px 12px;

  @media (max-width: 768px) {
    border-radius: 0;
  }
`;

const Input = styled.input<{ $isDarkTheme?: boolean; $accentColor?: string }>`
  flex: 1;
  border: 1px solid ${(props) => (props.$isDarkTheme ? "#333" : "#e0e0e0")};
  border-radius: 8px;
  padding: 10px 12px;
  font-size: 14px;
  background: ${(props) => (props.$isDarkTheme ? "#2a2a2a" : "#f5f5f5")};
  color: ${(props) => (props.$isDarkTheme ? "white" : "black")};
  caret-color: ${(props) =>
    props.$accentColor || (props.$isDarkTheme ? "#ffffff" : "#1a1a1a")};
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: ${(props) => props.$accentColor || "#ff006e"};
  }

  &::placeholder {
    color: ${(props) => (props.$isDarkTheme ? "#888" : "#999")};
  }
`;

const SendButton = styled.button<{ $accentColor?: string; $disabled: boolean }>`
  background: ${(props) => props.$accentColor || "#ff006e"};
  color: white;
  border: none;
  border-radius: 8px;
  padding: 10px 16px;
  font-size: 14px;
  font-weight: 500;
  cursor: ${(props) => (props.$disabled ? "not-allowed" : "pointer")};
  transition:
    transform 0.2s,
    box-shadow 0.2s,
    opacity 0.2s;
  opacity: ${(props) => (props.$disabled ? 0.6 : 1)};

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }
`;

const WelcomeMessage = styled.div<{ $isDarkTheme?: boolean }>`
  text-align: center;
  padding: 20px;
  color: ${(props) => (props.$isDarkTheme ? "#aaa" : "#666")};
  font-size: 14px;

  strong {
    display: block;
    font-size: 16px;
    color: ${(props) => (props.$isDarkTheme ? "white" : "black")};
    margin-bottom: 8px;
  }
`;

export const ChatModal: React.FC<ChatModalProps> = memo(
  ({
    isOpen,
    onClose,
    theme,
    isDarkTheme = true,
    onSendMessage,
    onContact,
  }) => {
    const [messages, setMessages] = useState<Message[]>([]);
    const [input, setInput] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [sessionId, setSessionId] = useState<string>("");
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const modalRef = useRef<HTMLDivElement>(null);
    const streamRef = useRef<ReturnType<typeof setInterval> | null>(null);

    // Initialize session
    useEffect(() => {
      if (isOpen && !sessionId) {
        setSessionId(
          `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        );
        if (messages.length === 0) {
          setMessages([
            {
              id: "welcome",
              type: "bot",
              content:
                "👋 Hi! I'm Rexford's AI Assistant. Ask me about his experience, skills, projects, or anything else!",
              source: "profile",
              timestamp: Date.now(),
            },
          ]);
        }
      }
    }, [isOpen, sessionId, messages.length]);

    const scrollToBottom = useCallback(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, []);

    useEffect(() => {
      scrollToBottom();
    }, [messages, scrollToBottom]);

    useEffect(() => {
      if (isOpen) {
        requestAnimationFrame(() => inputRef.current?.focus());
      }
    }, [isOpen]);

    // Close on Escape and trap focus within the modal while open.
    useEffect(() => {
      if (!isOpen) return;

      const handleKeyDown = (event: KeyboardEvent) => {
        if (event.key === "Escape") {
          event.preventDefault();
          onClose();
          return;
        }

        if (event.key !== "Tab") return;

        const modal = modalRef.current;
        if (!modal) return;

        const focusable = modal.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])',
        );
        if (focusable.length === 0) return;

        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        const active = document.activeElement;

        if (event.shiftKey && active === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && active === last) {
          event.preventDefault();
          first.focus();
        }
      };

      document.addEventListener("keydown", handleKeyDown);
      return () => document.removeEventListener("keydown", handleKeyDown);
    }, [isOpen, onClose]);

    useEffect(() => {
      return () => {
        if (streamRef.current) {
          clearInterval(streamRef.current);
        }
      };
    }, []);

    const updateMessageContent = useCallback(
      (messageId: string, content: string) => {
        setMessages((prev) =>
          prev.map((msg) => (msg.id === messageId ? { ...msg, content } : msg)),
        );
      },
      [],
    );

    const renderMessageContent = useCallback(
      (
        content: string,
        messageId: string,
        messageType: Message["type"],
      ): ReactNode => {
        const normalizedContent = content.replace(/\r/g, "\n");
        if (!normalizedContent.trim()) {
          return <p>&nbsp;</p>;
        }

        if (messageType !== "bot") {
          const collapsedUserContent = normalizedContent
            .split("\n")
            .map((line) => line.trim())
            .filter(Boolean)
            .join(" ")
            .replace(/\s{2,}/g, " ")
            .trim();

          const userText = collapsedUserContent || normalizedContent.trim();

          return <p key={`${messageId}-plain`}>{userText}</p>;
        }

        const lines = normalizedContent.split("\n");
        const fragmentedLines =
          lines.length > 1 && lines.every((line) => line.trim().length <= 1);

        if (fragmentedLines) {
          const collapsed = lines
            .map((line) => {
              const trimmed = line.trim();
              if (trimmed.length === 0) {
                return " ";
              }
              return trimmed;
            })
            .join("")
            .replace(/\s{2,}/g, " ")
            .trim();

          if (collapsed.length) {
            return <p key={`${messageId}-collapsed`}>{collapsed}</p>;
          }
          return <p key={`${messageId}-collapsed`}>&nbsp;</p>;
        }

        const elements: ReactNode[] = [];
        let listBuffer: Array<{ text: string; offset: number }> = [];
        let charOffset = 0;

        const flushList = (keySuffix: string) => {
          if (listBuffer.length === 0) return;
          elements.push(
            <ul key={`${messageId}-list-${keySuffix}`}>
              {listBuffer.map((item) => (
                <li key={`${messageId}-li-${item.offset}`}>{item.text}</li>
              ))}
            </ul>,
          );
          listBuffer = [];
        };

        lines.forEach((line) => {
          const trimmed = line.trim();
          const currentOffset = charOffset;
          charOffset += line.length + 1;

          if (!trimmed) {
            flushList(`${currentOffset}-blank`);
            elements.push(
              <p key={`${messageId}-blank-${currentOffset}`}>&nbsp;</p>,
            );
            return;
          }

          if (/^[-*]\s+/.test(trimmed)) {
            listBuffer.push({
              text: trimmed.replace(/^[-*]\s+/, ""),
              offset: currentOffset,
            });
          } else {
            flushList(`${currentOffset}-list`);
            elements.push(
              <p key={`${messageId}-p-${currentOffset}`}>{line}</p>,
            );
          }
        });

        flushList("final");

        if (elements.length === 0) {
          return <p key={`${messageId}-fallback`}>{normalizedContent}</p>;
        }

        return elements;
      },
      [],
    );

    const streamBotResponse = useCallback(
      (messageId: string, fullText: string) => {
        if (streamRef.current) {
          clearInterval(streamRef.current);
        }

        if (fullText.length <= 80) {
          updateMessageContent(messageId, fullText);
          streamRef.current = null;
          return;
        }

        const chunks = fullText.split(/(\s+)/);
        let index = 0;
        let buffer = "";

        streamRef.current = setInterval(() => {
          buffer += chunks[index] || "";
          updateMessageContent(messageId, buffer);
          index += 1;
          if (index >= chunks.length) {
            if (streamRef.current) {
              clearInterval(streamRef.current);
              streamRef.current = null;
            }
          }
        }, 25);
      },
      [updateMessageContent],
    );

    const handleSendMessage = useCallback(async () => {
      if (!input.trim() || isLoading) return;

      const userMessage = input.trim();
      setInput("");
      inputRef.current?.focus();

      // Add user message
      const userMsg: Message = {
        id: `msg_${Date.now()}`,
        type: "user",
        content: userMessage,
        timestamp: Date.now(),
      };

      setMessages((prev) => [...prev, userMsg]);
      setIsLoading(true);

      try {
        const response = await onSendMessage(userMessage);

        const botMsg: Message = {
          id: `msg_${Date.now()}_bot`,
          type: "bot",
          content: "",
          source: "profile",
          timestamp: Date.now(),
        };

        const botId = botMsg.id;
        setMessages((prev) => [...prev, botMsg]);
        streamBotResponse(botId, response);
      } catch (error) {
        handleError(error, {
          component: "ChatModal",
          operation: "handleSendMessage",
        });
        const errorMsg: Message = {
          id: `msg_${Date.now()}_error`,
          type: "bot",
          content: getUserFriendlyErrorMessage(error),
          timestamp: Date.now(),
        };
        setMessages((prev) => [...prev, errorMsg]);
      } finally {
        setIsLoading(false);
      }
    }, [input, isLoading, onSendMessage, streamBotResponse]);

    const handleKeyPress = useCallback(
      (e: React.KeyboardEvent) => {
        if (e.key === "Enter" && !e.shiftKey) {
          e.preventDefault();
          handleSendMessage();
        }
      },
      [handleSendMessage],
    );

    const handleInputChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        setInput(e.target.value);
      },
      [],
    );

    const handleOverlayClick = useCallback(
      (e: React.MouseEvent<HTMLDivElement>) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      },
      [onClose],
    );

    const handleModalClick = useCallback(
      (e: React.MouseEvent<HTMLDivElement>) => {
        e.stopPropagation();
      },
      [],
    );

    if (!isOpen) return null;

    return (
      <Overlay $isOpen={isOpen} onClick={handleOverlayClick}>
        <ModalContainer
          ref={modalRef}
          $isDarkTheme={isDarkTheme}
          $isOpen={isOpen}
          onClick={handleModalClick}
          role="dialog"
          aria-modal="true"
          aria-labelledby="chat-modal-title"
        >
          <Header $isDarkTheme={isDarkTheme} $accentColor={theme?.accentColor}>
            <HeaderTitle id="chat-modal-title">
              Chat with Rexford AI
            </HeaderTitle>
            <CloseButton
              onClick={onClose}
              title="Close chat"
              aria-label="Close chat"
            >
              ✕
            </CloseButton>
          </Header>

          <MessageContainer
            $isDarkTheme={isDarkTheme}
            aria-live="polite"
            aria-atomic="false"
          >
            {messages.length === 0 && (
              <WelcomeMessage $isDarkTheme={isDarkTheme}>
                <strong>Welcome! 👋</strong> Ask me about my experience, skills,
                projects, or anything else!
              </WelcomeMessage>
            )}

            {messages.map((msg) => (
              <MessageBubble
                key={msg.id}
                $isUser={msg.type === "user"}
                $isDarkTheme={isDarkTheme}
                $accentColor={theme?.accentColor}
              >
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: msg.type === "user" ? "flex-end" : "flex-start",
                    width: "100%",
                  }}
                >
                  <MessageText
                    $isUser={msg.type === "user"}
                    $isDarkTheme={isDarkTheme}
                    $accentColor={theme?.accentColor}
                  >
                    {renderMessageContent(msg.content, msg.id, msg.type)}
                  </MessageText>
                </div>
              </MessageBubble>
            ))}

            {isLoading && (
              <MessageBubble
                $isUser={false}
                $isDarkTheme={isDarkTheme}
                $accentColor={theme?.accentColor}
              >
                <TypingIndicator>
                  <span></span>
                  <span></span>
                  <span></span>
                </TypingIndicator>
              </MessageBubble>
            )}

            <div ref={messagesEndRef} />
          </MessageContainer>

          <InputContainer $isDarkTheme={isDarkTheme}>
            <Input
              type="text"
              name="chat-message"
              autoComplete="off"
              placeholder="Ask me anything…"
              value={input}
              onChange={handleInputChange}
              onKeyPress={handleKeyPress}
              $isDarkTheme={isDarkTheme}
              $accentColor={theme?.accentColor}
              ref={inputRef}
              aria-label="Chat message input"
            />
            <SendButton
              onClick={handleSendMessage}
              disabled={isLoading || !input.trim()}
              $accentColor={theme?.accentColor}
              $disabled={isLoading || !input.trim()}
              aria-label="Send message"
            >
              Send
            </SendButton>
          </InputContainer>
        </ModalContainer>
      </Overlay>
    );
  },
);

export default ChatModal;
